import React, { useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { BUCKET_LABEL, HELMET_BUCKETS } from "@utils/helmetDerive.mjs";
import { ALERT_COLOR, ALERT_ICON_SVG } from "./AlertIcon";

// The app's severity colours (CustomCharts stackedBarChart).
const SERIES_STYLE = {
  low: { color: "#31B452", legend: "below 42" },
  medium: { color: "#FFC83D", legend: "42–99" },
  high: { color: "#d9534f", legend: "100+" },
} as const;
const SELECTED_BAND = "rgba(22,119,255,.08)";
export const CHART_HEIGHT = 440;

interface DayCount {
  day: string;
  low: number;
  medium: number;
  high: number;
  total: number;
  alerts: number;
}

interface Props {
  counts: DayCount[];
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
}

const alertLabel = (alerts: number) =>
  alerts ? ` <span style="color:${ALERT_COLOR};white-space:nowrap">${ALERT_ICON_SVG} ${alerts}</span>` : "";

// The chart keeps the Highcharts defaults of the GPS and Testing charts (StackedBarChartMapper): the title,
// the legend, the axes and the column padding. Only the selected-day band and the alert labels are its own.
export function HelmetDailyChart({ counts, selectedDay, onSelectDay }: Props) {
  // Highcharts calls the handlers on the chart it built first, so they read the latest props from a ref.
  const latest = useRef({ counts, onSelectDay });
  latest.current = { counts, onSelectDay };

  const options = useMemo<Highcharts.Options>(() => {
    const days = counts.map((c) => c.day);
    const selected = selectedDay ? days.indexOf(selectedDay) : -1;
    // A select inside a Highcharts event rebuilds the chart while Highcharts still reads its hover point.
    const select = (index: number) => {
      const day = latest.current.counts[index]?.day;
      if (day) setTimeout(() => latest.current.onSelectDay(day));
    };

    return {
      chart: {
        type: "column",
        animation: false,
        height: CHART_HEIGHT,
        events: {
          render() {
            Object.values(this.xAxis[0].ticks).forEach((tick: any) => {
              const label = tick.label?.element as HTMLElement | undefined;
              if (!label || label.dataset.helmetWired) return;
              label.dataset.helmetWired = "true";
              label.style.cursor = "pointer";
              label.addEventListener("click", () => select(tick.pos));
            });
          },
        },
      },
      title: { text: "Daily Impacts" },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        categories: days.map((d) => d.slice(5)),
        gridLineWidth: 2,
        tickWidth: 1,
        tickmarkPlacement: days.length > 10 ? "on" : "between",
        plotBands: selected >= 0 ? [{ from: selected - 0.5, to: selected + 0.5, color: SELECTED_BAND }] : [],
        labels: {
          rotation: days.length > 20 ? -90 : days.length > 10 ? -45 : 0,
          formatter() {
            return this.pos === selected
              ? `<span style="color:#1677ff;font-weight:700">${this.value}</span>`
              : `${this.value}`;
          },
        },
      },
      yAxis: {
        title: { text: null },
        stackLabels: {
          enabled: true,
          useHTML: true,
          formatter() {
            if (!this.total) return "";
            const alerts = counts[this.x as number]?.alerts ?? 0;
            return `<span style="white-space:nowrap">${this.total.toLocaleString("en-US")}${alertLabel(alerts)}</span>`;
          },
        },
      },
      legend: { enabled: true, verticalAlign: "top", layout: "horizontal" },
      tooltip: {
        shared: true,
        useHTML: true,
        // The stack labels are HTML, drawn above the SVG. An outside tooltip is drawn above both.
        outside: true,
        formatter() {
          const index = (this as any).points[0].point.x as number;
          const count = counts[index];
          const rows = [...(this as any).points].reverse().map((p: any) => `<br/>${p.series.name}: ${p.y}`).join("");
          return `<b>${count.day}</b>${rows}<br/>${ALERT_ICON_SVG} Alerts: ${count.alerts}<br/>Total: ${count.total}`;
        },
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderWidth: 0,
          groupPadding: 0.05,
          pointPadding: 0.05,
          cursor: "pointer",
          animation: false,
          point: { events: { click() { select(this.x as number); } } },
        },
      },
      series: HELMET_BUCKETS.map((bucket: "low" | "medium" | "high", index: number) => ({
        type: "column" as const,
        name: `${BUCKET_LABEL[bucket]} (load ${SERIES_STYLE[bucket].legend})`,
        legendIndex: index,
        color: SERIES_STYLE[bucket].color,
        data: counts.map((c) => c[bucket]),
      })),
    };
  }, [counts, selectedDay]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
